'use client'

import { useEffect, useState } from 'react'

type Edge = 'top' | 'bottom'

type UseContentUnderEdgeOptions = {
  edge: Edge
  /** Closest ancestor of the sentinel that scrolls. Defaults to the viewport. */
  rootSelector?: string
}

function isPastEdge(entry: IntersectionObserverEntry, edge: Edge) {
  if (entry.isIntersecting) {
    return false
  }

  if (!entry.rootBounds) {
    return true
  }

  if (edge === 'top') {
    return entry.boundingClientRect.top < entry.rootBounds.top
  }

  return entry.boundingClientRect.bottom > entry.rootBounds.bottom
}

// True while content has scrolled under a sticky edge (a header, cap or
// footer). Watches a zero-height sentinel placed where the content meets that
// edge at rest, so there is no scroll listener. The refs are callback refs, so
// the observer starts whenever both elements mount, and it is rebuilt when the
// edge's height changes so the cut line tracks it.
export function useContentUnderEdge({
  edge,
  rootSelector,
}: UseContentUnderEdgeOptions) {
  const [sentinel, sentinelRef] = useState<HTMLElement | null>(null)
  const [edgeElement, edgeRef] = useState<HTMLElement | null>(null)
  const [edgeHeight, setEdgeHeight] = useState(0)
  const [isContentUnder, setIsContentUnder] = useState(false)

  useEffect(() => {
    if (!edgeElement) {
      return
    }

    const observer = new ResizeObserver(() => {
      setEdgeHeight(edgeElement.offsetHeight)
    })

    observer.observe(edgeElement)

    return () => observer.disconnect()
  }, [edgeElement])

  useEffect(() => {
    if (!sentinel || !edgeElement) {
      return
    }

    const root = rootSelector ? sentinel.closest(rootSelector) : null

    if (rootSelector && !root) {
      return
    }

    const inset = `-${edgeHeight}px`
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsContentUnder(isPastEdge(entry, edge))
      },
      {
        root,
        rootMargin:
          edge === 'top' ? `${inset} 0px 0px 0px` : `0px 0px ${inset} 0px`,
      },
    )

    observer.observe(sentinel)

    return () => observer.disconnect()
  }, [sentinel, edgeElement, edgeHeight, edge, rootSelector])

  return { sentinelRef, edgeRef, isContentUnder }
}
